import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Toolbar, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  CircularProgress, 
  Snackbar, 
  Alert, 
  CssBaseline, 
  Stepper, 
  Step, 
  StepLabel, 
  TextField, 
  MenuItem, 
  FormControl, 
  Select, 
  Tabs, 
  Tab, 
  FormControlLabel, 
  Checkbox, 
  Link, 
  Modal,
  Card,
  CardContent,
  InputLabel,
  Fade,
  Slide,
  Divider,
  Paper,
  useMediaQuery,
} from '@mui/material';

import { styled } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material/styles';
import { 
  MonetizationOn, 
  AccountBalance, 
  CheckCircleOutline,
  BookmarkAdded,
  ArrowForward,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useMutation } from 'react-query';
import {useTheme} from '@mui/material/styles';
import HomeHeader from '../../Components/HomeHeader';
import { replaceConsoleError } from '../../Utils/toastNotifications';
import HomeFooter from '../../Components/HomeFooter';
import CreatorOnboard from '../../CreatorUser/creatorOnboard'; 
import { validateEmail, validatePassword, capitalizeFirstLetter } from '../../Utils';
import { lightTheme } from '../../Utils/baseTheme';

import routes from '../../Config/routes';
import client from '../../API';

const FullScreenOverlay = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.9)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1300,
  backdropFilter: 'blur(8px)',
  padding: theme.spacing(2),
  overflow: 'auto',
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  fontWeight: 600,
  padding: '12px 24px',
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: '14px',
  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  [theme.breakpoints.up('sm')]: {
    padding: '14px 32px',
    fontSize: '16px',
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 30px rgba(102, 126, 234, 0.6)',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  '&:disabled': {
    background: '#e2e8f0',
    color: '#94a3b8',
    transform: 'none',
    boxShadow: 'none',
  }
}));

const ModernTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#f8fafc',
    transition: 'all 0.3s ease',
    fontSize: '14px',
    [theme.breakpoints.up('sm')]: {
      fontSize: '16px',
    },
    '& fieldset': {
      borderColor: '#e2e8f0',
      borderWidth: '1px',
    },
    '&:hover': {
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#e2e8f0',
      },
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#667eea',
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    fontSize: '14px',
    [theme.breakpoints.up('sm')]: {
      fontSize: '16px',
    },
  },
}));

const StepCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
  border: '1px solid #f1f5f9',
  overflow: 'hidden',
  [theme.breakpoints.up('md')]: {
    borderRadius: '20px',
  },
}));

const AllOnboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const convParam = searchParams.get('conv');
  const emailParam = searchParams.get('email');
  const tabParam = searchParams.get('tab');
  const usernameParam = searchParams.get('username') || '';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [tabValue, setTabValue] = useState(
    tabParam === 'creator' ? 'creator' : 'brand'
  );

  const isTabParamValid = tabParam === 'creator' || tabParam === 'brand';
  const [showRoleDialog, setShowRoleDialog] = useState(!isTabParamValid && !emailParam);
  const [showEmailCompletionDialog, setShowEmailCompletionDialog] = useState(!!emailParam);

  useEffect(() => {
    if (emailParam) {
      setShowEmailCompletionDialog(true);
      setShowRoleDialog(false);
    } else if (tabParam === 'creator' || tabParam === 'brand') {
      setTabValue(tabParam);
      setShowRoleDialog(false);
      setShowEmailCompletionDialog(false);
    } else {
      setShowRoleDialog(true);
      setShowEmailCompletionDialog(false);
    }
  }, [tabParam, emailParam]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    navigate({
      pathname: window.location.pathname,
      search: `?tab=${newValue}${convParam ? `&conv=${convParam}` : ''}${emailParam ? `&email=${emailParam}` : ''}`
    });
  };

  const handleChooseRole = (role) => {
    navigate({
      pathname: window.location.pathname,
      search: `?tab=${role}${convParam ? `&conv=${convParam}` : ''}${emailParam ? `&email=${emailParam}` : ''}`
    });
  };

  const handleEmailCompletionContinue = () => {
    setShowEmailCompletionDialog(false);
    if (!isTabParamValid) {
      setShowRoleDialog(true);
    }
  };

  const RoleCard = ({ role, title, description, primary = false, onClick }) => (
    <Card 
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        mb: 2,
        border: '1px solid',
        borderColor: primary ? 'rgba(102, 126, 234, 0.3)' : 'rgba(0, 0, 0, 0.08)',
        borderRadius: '16px',
        background: primary 
          ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
          : '#ffffff',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          // background: role === 'brand' 
          //   ? 'linear-gradient(90deg, #667eea, #764ba2)'
          //   : 'linear-gradient(90deg, #10b981, #34d399)',
          opacity: primary ? 1 : 0,
          transition: 'opacity 0.3s ease',
        },
        '&:hover': {
          transform: { xs: 'translateY(-4px)', md: 'translateY(-8px)' },
          boxShadow: primary 
            ? '0 25px 50px rgba(102, 126, 234, 0.25)' 
            : '0 25px 50px rgba(0, 0, 0, 0.12)',
          borderColor: role === 'brand' ? 'rgba(102, 126, 234, 0.5)' : 'rgba(16, 185, 129, 0.5)',
          '&::before': {
            opacity: 1,
          },
        },
      }}
      onClick={() => onClick(role)}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 }, textAlign: 'left' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, sm: 2 }, mb: { xs: 1.5, sm: 2 } }}>
          <Box 
            sx={{ 
              p: { xs: 1, sm: 1.5 },
              borderRadius: '12px',
              background: role === 'brand' 
                ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(52, 211, 153, 0.1) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {role === 'brand' ? (
              <AccountBalance sx={{ fontSize: { xs: 24, sm: 28 }, color: '#667eea' }} />
            ) : (
              <MonetizationOn sx={{ fontSize: { xs: 24, sm: 28 }, color: '#10b981' }} />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                color: '#1e293b',
                fontSize: { xs: '1rem', sm: '1.1rem' },
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 600, 
                color: role === 'brand' ? '#667eea' : '#10b981',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: { xs: '0.6rem', sm: '0.65rem' },
              }}
            >
              {role === 'brand' ? 'Payer Account' : 'Payee Account'}
            </Typography>
          </Box>
        </Box>
        
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#64748b', 
            lineHeight: 1.6, 
            mb: { xs: 1.5, sm: 2 },
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
          }}
        >
          {description}
        </Typography>
        
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            pt: { xs: 1.5, sm: 2 },
            borderTop: '1px solid rgba(0, 0, 0, 0.06)',
          }}
        >
          <Box 
            sx={{ 
              width: 6, 
              height: 6, 
              borderRadius: '50%', 
              bgcolor: role === 'brand' ? '#e11d48' : '#10b981',
              flexShrink: 0,
            }} 
          />
          <Typography 
            variant="caption" 
            sx={{ 
              fontWeight: 500, 
              color: role === 'brand' ? '#e11d48' : '#10b981',
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
            }}
          >
            {role === 'brand' ? 'Funds campaigns (Money Out)' : 'Earn from campaigns (Money In)'}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <HomeHeader />
        <Toolbar />

        {/* Email Completion Dialog */}
        <Modal open={showEmailCompletionDialog} onClose={() => setShowEmailCompletionDialog(false)}>
          <Fade in={showEmailCompletionDialog}>
            <FullScreenOverlay>
              <Box sx={{
                background: 'white',
                borderRadius: { xs: '16px', sm: '24px' },
                padding: { xs: '24px', sm: '32px', md: '48px' },
                textAlign: 'center',
                maxWidth: '520px',
                width: '100%',
                maxHeight: { xs: '90vh', sm: 'auto' },
                overflow: 'auto',
                boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mb: { xs: 2, sm: 3 },
                  '& svg': {
                    fontSize: { xs: 48, sm: 64 },
                    color: '#22c55e'
                  }
                }}>
                  <CheckCircleOutline />
                </Box>
                
                <Typography variant="h4" sx={{ 
                  mb: { xs: 1.5, sm: 2 }, 
                  fontWeight: 800, 
                  color: '#1e293b',
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                }}>
                  Booking Received!
                </Typography>
                
                <Typography variant="h6" sx={{ 
                  color: 'text.secondary', 
                  mb: { xs: 3, sm: 4 }, 
                  fontWeight: 400, 
                  lineHeight: 1.5,
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' },
                }}>
                  Now, to complete and manage your booking, complete your account registration here.
                </Typography>

                <Paper 
                  elevation={0}
                  sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: { xs: 3, sm: 4 },
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: { xs: '12px', sm: '16px' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: { xs: 1, sm: 2 } }}>
                    <BookmarkAdded sx={{ color: '#667eea', mr: 1, fontSize: { xs: 20, sm: 24 } }} />
                    <Typography variant="subtitle1" sx={{ 
                      fontWeight: 600, 
                      color: '#667eea',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    }}>
                      Account Email
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ 
                    fontWeight: 600, 
                    color: '#1e293b',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    wordBreak: 'break-all',
                  }}>
                    {emailParam}
                  </Typography>
                </Paper>

                <Divider sx={{ my: { xs: 2, sm: 3 } }} />

                <Typography variant="body2" sx={{ 
                  color: 'text.secondary', 
                  mb: { xs: 3, sm: 4 },
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                }}>
                  Complete your registration to access your dashboard, manage payments, and track your collaborations.
                </Typography>

                <GradientButton 
                  onClick={handleEmailCompletionContinue}
                  endIcon={<ArrowForward />}
                  size="large"
                  fullWidth={isMobile}
                  sx={{ mb: { xs: 2, sm: 3 } }}
                >
                  Continue to Registration
                </GradientButton>

                <Typography variant="body2" sx={{ 
                  color: 'text.secondary',
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                }}>
                  Already have an account?{' '}
                  <Link component={RouterLink} to={routes.login} sx={{ 
                    fontWeight: 600, 
                    color: '#667eea',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}>
                    Sign in here
                  </Link>
                </Typography>
              </Box>
            </FullScreenOverlay>
          </Fade>
        </Modal>

        {/* Role Selection Dialog */}
        <Modal open={showRoleDialog} onClose={() => setShowRoleDialog(false)}>
          <Fade in={showRoleDialog}>
            <FullScreenOverlay sx={{ bgcolor: 'transparent' }}>
              <Box sx={{
                background: 'rgba(235, 235, 235, 0.95)',
                borderRadius: '12px',
                padding: { xs: '24px', sm: '32px', md: '48px' },
                textAlign: 'center',
                maxWidth: '600px',
                width: '100%',
                maxHeight: { xs: '90vh', sm: 'auto' },
                overflow: 'auto',
                boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
              }}>
                <Typography variant="h3" sx={{ 
                  mb: { xs: 1, sm: 2 }, 
                  fontWeight: 600, 
                  color: '#1e293b', 
                  fontFamily: 'Moret Variable',
                  fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
                }}>
                  Welcome to Blitz
                </Typography>
                <Typography variant="h6" sx={{ 
                  color: 'text.secondary', 
                  mb: { xs: 3, sm: 4, md: 6 }, 
                  fontWeight: 400,
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' },
                }}>
                  Choose your account type to get started
                </Typography>
                
                <Box sx={{ gap: 3, mb: { xs: 3, sm: 4, md: 6 } }}>
                  <RoleCard
                    role="brand"
                    title="Brand Account"
                    description="Create campaigns, hire creators, and grow your brand with influencer marketing."
                    primary={true}
                    onClick={handleChooseRole}
                  />
                  <RoleCard
                    role="creator"
                    title="Creator Account"
                    description="Monetize your content, get hired by brands, and grow your creator business."
                    onClick={handleChooseRole}
                  />
                </Box>

                <Typography variant="body2" sx={{ 
                  color: 'text.secondary',
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                }}>
                  Already have an account?{' '}
                  <Link component={RouterLink} to={routes.login} sx={{ 
                    fontWeight: 600, 
                    color: '#667eea',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}>
                    Sign in here
                  </Link>
                </Typography>
              </Box>
            </FullScreenOverlay>
          </Fade>
        </Modal>

        <Box sx={{ flexGrow: 1, py: { xs: 2, sm: 3, md: 4 }, background: theme.palette.background.default }}>
          <Box maxWidth="lg" sx={{ mx: 'auto', px: { xs: 2, sm: 3, md: 4 } }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant={isMobile ? 'fullWidth' : 'standard'}
              centered={!isMobile}
              sx={{
                mb: { xs: 3, sm: 4, md: 6 },
                '& .MuiTabs-indicator': {
                  height: { xs: '3px', sm: '4px' },
                  borderRadius: '4px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: { xs: '14px', sm: '16px', md: '18px' },
                  fontWeight: 600,
                  minWidth: { xs: 'auto', sm: 150, md: 200 },
                  px: { xs: 1, sm: 2, md: 3 },
                  '&.Mui-selected': {
                    color: '#667eea',
                  }
                }
              }}
            >
              <Tab label={isMobile ? 'Brand' : 'Brand Registration'} value="brand" />
              <Tab label={isMobile ? 'Creator' : 'Creator Registration'} value="creator" />
            </Tabs>

            <Slide direction="up" in={true} timeout={600}>
              <StepCard sx={{ background: 'rgba(245,245,245,0.8)', borderRadius: '12px' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3, md: 6 } }}>
                  {tabValue === 'brand' && (
                    <BrandRegisterForm
                      conversationIdParam={convParam}
                      inviteEmailParam={emailParam}
                    />
                  )}
                  {tabValue === 'creator' && (
                    <CreatorOnboard
                      conversationIdParam={convParam}
                      inviteEmailParam={emailParam}
                      prefilledUsername={usernameParam}
                    />
                  )}
                </CardContent>
              </StepCard>
            </Slide>
          </Box>
        </Box>

        <HomeFooter sx={{ mt: 'auto', py: { xs: 4, md: 6 }, background: 'white' }} />
      </Box>
    </ThemeProvider>
  );
};

const BrandRegisterForm = ({ conversationIdParam, inviteEmailParam }) => {
  const navigate = useNavigate();
  const { ref } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const referringUserId = ref && !isNaN(Number(ref)) ? Number(ref) : null;
  const referralSource = referringUserId === null ? ref : '';

  const [conversationId] = useState(conversationIdParam || null);
  const [activeStep, setActiveStep] = useState(0);
  const steps = isMobile 
    ? ['Personal', 'Company', 'Business'] 
    : ['Personal Information', 'Company Details (Required)', 'Business Information'];

  const [userInfo, setUserInfo] = useState({
    firstName: '',
    lastName: '',
    email: inviteEmailParam || '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    companyName: '',
    streetAddress: '',
    city: '',
    state: '',
    country: '',
    industry: '',
    bio: '',
    businessType: '',
    isAgency: '',
    monthlySpend: '',
    teamAccounts: '',
    paymentMethod: '',
    referralSource: referralSource,
    referringUserId: referringUserId,
    termsAccepted: false
  });

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenSnackbar(false);
  };

  const { mutate: register, isLoading } = useMutation(client.companies.demoRegister, {
    onSuccess: () => {
      navigate(routes.login);
    },
    onError: (error) => {
      replaceConsoleError('Registration error', error);
      let errorMessage = 'Registration failed. Please try again.';
      if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Network connection error. Please check your internet connection.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  });

  const handleNext = () => {
    const requiredFields = getRequiredFieldsForStep(activeStep);
    for (const field of requiredFields) {
      if (!userInfo[field] || userInfo[field].toString().trim() === '') {
        setSnackbarMessage(`${getFieldLabel(field)} is required`);
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        return;
      }
    }

    if (activeStep === 0) {
      if (!validateEmail(userInfo.email)) {
        setSnackbarMessage('Please enter a valid email address');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        return;
      }
      if (userInfo.password.length < 12) {
        setSnackbarMessage('Password must be at least 12 characters long');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        return;
      }
      if (userInfo.password !== userInfo.confirmPassword) {
        setSnackbarMessage('Passwords do not match');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        return;
      }
    }

    if (activeStep === steps.length - 1) {
      handleRegister();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleRegister = () => {
    if (!userInfo.termsAccepted) {
      setSnackbarMessage('Please accept the terms and conditions to continue');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    const registrationData = {
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      email: userInfo.email,
      password: userInfo.password,
      confirmPassword: userInfo.confirmPassword,
      phoneNumber: userInfo.phoneNumber,
      referralSource: userInfo.referralSource,
      referringUserId: userInfo.referringUserId,
      companyName: userInfo.companyName.trim(),
      streetAddress: userInfo.streetAddress,
      city: userInfo.city,
      state: userInfo.state,
      country: userInfo.country,
      industry: userInfo.industry,
      bio: userInfo.bio,
      businessType: userInfo.businessType,
      isAgency: userInfo.isAgency,
      monthlySpend: userInfo.monthlySpend,
      teamAccounts: userInfo.teamAccounts,
      paymentMethod: userInfo.paymentMethod
    };

    if (!registrationData.companyName) {
      setSnackbarMessage('Company Name is required');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    register(registrationData);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setUserInfo((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const getRequiredFieldsForStep = (step) => {
    switch (step) {
      case 0:
        return ['firstName', 'lastName', 'email', 'password', 'confirmPassword', 'phoneNumber'];
      case 1:
        return ['companyName'];
      case 2:
        return [];
      default:
        return [];
    }
  };

  const getFieldLabel = (field) => {
    const labels = {
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email Address',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      phoneNumber: 'Phone Number',
      companyName: 'Company Name',
      industry: 'Industry',
      businessType: 'Business Structure',
      isAgency: 'Agency Status',
      monthlySpend: 'Expected Monthly Spend',
      teamAccounts: 'Team Size',
      paymentMethod: 'Preferred Payment Method',
    };
    return labels[field] || field;
  };

  const industryOptions = [
    'Fashion & Beauty', 'Technology', 'Food & Beverage', 
    'Health & Fitness', 'Travel & Tourism', 'Finance', 
    'Education', 'Entertainment', 'Automotive',
    'Real Estate', 'Hospitality', 'Retail', 'Manufacturing', 
    'Marketing Agency', 'E-commerce', 'Gaming', 'Sports', 
    'Home & Garden', 'Pets', 'Other'
  ];

  const countryOptions = [
    'United States', 'Canada', 'United Kingdom', 'Australia', 'Germany',
    'France', 'Italy', 'Spain', 'Japan', 'India', 'Brazil', 'Mexico',
    'Netherlands', 'Sweden', 'Singapore', 'South Korea', 'Other'
  ];

  const selectStyles = {
    borderRadius: '12px',
    backgroundColor: '#f8fafc',
    fontSize: { xs: '14px', sm: '16px' },
    '&:hover': { backgroundColor: '#ffffff' },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#667eea',
        borderWidth: '2px',
      },
    },
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h5" sx={{ 
              mb: { xs: 2, sm: 3, md: 4 }, 
              fontWeight: 700, 
              color: '#1e293b',
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
            }}>
              Personal Information
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
              <ModernTextField
                disabled={isLoading}
                label="First Name"
                name="firstName"
                variant="outlined"
                value={userInfo.firstName}
                onChange={handleChange}
                required
                fullWidth
                size={isMobile ? 'small' : 'medium'}
              />
              <ModernTextField
                disabled={isLoading}
                label="Last Name"
                name="lastName"
                variant="outlined"
                value={userInfo.lastName}
                onChange={handleChange}
                required
                fullWidth
                size={isMobile ? 'small' : 'medium'}
              />
            </Box>
            <ModernTextField
              disabled={isLoading || !!inviteEmailParam}
              label="Email Address"
              name="email"
              variant="outlined"
              type="email"
              value={userInfo.email}
              onChange={handleChange}
              required
              fullWidth
              size={isMobile ? 'small' : 'medium'}
              sx={{ mb: { xs: 2, sm: 3 } }}
              helperText={inviteEmailParam ? "Email address from your booking" : ""}
            />
            <ModernTextField
              disabled={isLoading}
              label="Phone Number"
              name="phoneNumber"
              variant="outlined"
              value={userInfo.phoneNumber}
              onChange={handleChange}
              required
              fullWidth
              size={isMobile ? 'small' : 'medium'}
              sx={{ mb: { xs: 2, sm: 3 } }}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 2, sm: 3 } }}>
              <ModernTextField
                disabled={isLoading}
                label="Password"
                name="password"
                variant="outlined"
                type="password"
                value={userInfo.password}
                onChange={handleChange}
                required
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                helperText="At least 12 characters"
              />
              <ModernTextField
                disabled={isLoading}
                label="Confirm Password"
                name="confirmPassword"
                variant="outlined"
                type="password"
                value={userInfo.confirmPassword}
                onChange={handleChange}
                required
                fullWidth
                size={isMobile ? 'small' : 'medium'}
              />
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h5" sx={{ 
              mb: { xs: 2, sm: 3, md: 4 }, 
              fontWeight: 700, 
              color: '#1e293b',
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
            }}>
              Company Details
            </Typography>

            <ModernTextField
              disabled={isLoading}
              label="Company Name *"
              name="companyName"
              variant="outlined"
              value={userInfo.companyName}
              onChange={handleChange}
              required
              fullWidth
              size={isMobile ? 'small' : 'medium'}
              sx={{ mb: { xs: 2, sm: 3 } }}
            />
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
              <ModernTextField
                disabled={isLoading}
                label="Street Address"
                name="streetAddress"
                variant="outlined"
                value={userInfo.streetAddress}
                onChange={handleChange}
                fullWidth
                size={isMobile ? 'small' : 'medium'}
              />
              <ModernTextField
                disabled={isLoading}
                label="City"
                name="city"
                variant="outlined"
                value={userInfo.city}
                onChange={handleChange}
                fullWidth
                size={isMobile ? 'small' : 'medium'}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
              <ModernTextField
                disabled={isLoading}
                label="State/Province"
                name="state"
                variant="outlined"
                value={userInfo.state}
                onChange={handleChange}
                fullWidth
                size={isMobile ? 'small' : 'medium'}
              />
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                <InputLabel>Country</InputLabel>
                <Select
                  name="country"
                  value={userInfo.country}
                  onChange={handleChange}
                  label="Country"
                  sx={selectStyles}
                >
                  {countryOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <FormControl fullWidth size={isMobile ? 'small' : 'medium'} sx={{ mb: { xs: 2, sm: 3 } }}>
              <InputLabel>Industry</InputLabel>
              <Select
                name="industry"
                value={userInfo.industry}
                onChange={handleChange}
                label="Industry"
                sx={selectStyles}
              >
                {industryOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <ModernTextField
              disabled={isLoading}
              label="Tell us about your company"
              name="bio"
              variant="outlined"
              multiline
              rows={isMobile ? 3 : 4}
              value={userInfo.bio}
              onChange={handleChange}
              fullWidth
              placeholder="Brief description of your company and what you do..."
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h5" sx={{ 
              mb: { xs: 2, sm: 3, md: 4 }, 
              fontWeight: 700, 
              color: '#1e293b',
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
            }}>
              Business Information
            </Typography>

            <FormControl fullWidth size={isMobile ? 'small' : 'medium'} sx={{ mb: { xs: 2, sm: 3 } }}>
              <InputLabel>Business Structure</InputLabel>
              <Select
                name="businessType"
                value={userInfo.businessType}
                onChange={handleChange}
                label="Business Structure"
                sx={selectStyles}
              >
                <MenuItem value="Single Entity">Single Company</MenuItem>
                <MenuItem value="Multiple Entities">Multiple Companies</MenuItem>
                <MenuItem value="Holding Company">Holding Company</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size={isMobile ? 'small' : 'medium'} sx={{ mb: { xs: 2, sm: 3 } }}>
              <InputLabel>Are you an agency?</InputLabel>
              <Select
                name="isAgency"
                value={userInfo.isAgency}
                onChange={handleChange}
                label="Are you an agency?"
                sx={selectStyles}
              >
                <MenuItem value="Yes">Yes, we're an agency</MenuItem>
                <MenuItem value="No">No, we're a brand</MenuItem>
              </Select>
            </FormControl>

            <ModernTextField
              disabled={isLoading}
              label="Expected Monthly Spend ($)"
              name="monthlySpend"
              variant="outlined"
              value={userInfo.monthlySpend}
              onChange={handleChange}
              fullWidth
              size={isMobile ? 'small' : 'medium'}
              sx={{ mb: { xs: 2, sm: 3 } }}
              placeholder="e.g., $5,000"
            />

            <ModernTextField
              disabled={isLoading}
              label="Team Size"
              name="teamAccounts"
              variant="outlined"
              value={userInfo.teamAccounts}
              onChange={handleChange}
              fullWidth
              size={isMobile ? 'small' : 'medium'}
              sx={{ mb: { xs: 2, sm: 3 } }}
              placeholder="How many team members will use this account?"
            />

            <FormControl fullWidth size={isMobile ? 'small' : 'medium'} sx={{ mb: { xs: 3, sm: 4 } }}>
              <InputLabel>Preferred Payment Method</InputLabel>
              <Select
                name="paymentMethod"
                value={userInfo.paymentMethod}
                onChange={handleChange}
                label="Preferred Payment Method"
                sx={selectStyles}
              >
                <MenuItem value="Credit Card">Credit Card</MenuItem>
                <MenuItem value="Bank Transfer">Bank Transfer/ACH</MenuItem>
                <MenuItem value="PayPal">PayPal</MenuItem>
                <MenuItem value="Wire Transfer">Wire Transfer</MenuItem>
              </Select>
            </FormControl>

            <ModernTextField
              disabled={isLoading}
              label="How did you hear about us?"
              name="referralSource"
              variant="outlined"
              value={userInfo.referralSource}
              onChange={handleChange}
              fullWidth
              size={isMobile ? 'small' : 'medium'}
              sx={{ mb: { xs: 3, sm: 4 } }}
              placeholder="Google, friend referral, social media, etc."
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="termsAccepted"
                  checked={userInfo.termsAccepted}
                  onChange={handleChange}
                  disabled={isLoading}
                  required
                  size={isMobile ? 'small' : 'medium'}
                  sx={{ 
                    color: '#667eea',
                    '&.Mui-checked': { color: '#667eea' }
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  I agree to the{' '}
                  <Link
                    component={RouterLink}
                    to="/terms"
                    target="_blank"
                    rel="noopener"
                    sx={{ color: '#667eea', fontWeight: 600 }}
                  >
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link
                    component={RouterLink}
                    to="/privacy"
                    target="_blank"
                    rel="noopener"
                    sx={{ color: '#667eea', fontWeight: 600 }}
                  >
                    Privacy Policy
                  </Link>
                </Typography>
              }
            />
          </Box>
        );

      default:
        return null;
    }
  };

  const CustomStepIcon = ({ active, completed, icon }) => {
    return (
      <Box
        sx={{
          width: { xs: 32, sm: 40 },
          height: { xs: 32, sm: 40 },
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: completed ? '#667eea' : active ? '#e2e8f0' : '#f1f5f9',
          color: completed ? 'white' : active ? '#667eea' : '#94a3b8',
          fontWeight: 600,
          fontSize: { xs: '0.75rem', sm: '1rem' },
          border: active && !completed ? '2px solid #667eea' : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        {completed ? '✓' : icon}
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h5" sx={{ 
        fontWeight: 700, 
        color: '#1e293b', 
        textAlign: 'center', 
        mb: { xs: 2, sm: 3 },
        fontSize: { xs: '1.25rem', sm: '1.5rem' },
      }}>
        Brand Account Setup
      </Typography>
      
      <Stepper 
        activeStep={activeStep}
        alternativeLabel={isMobile}
        sx={{ mb: { xs: 4, sm: 6, md: 8 } }}
      >
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel
              StepIconComponent={(props) => (
                <CustomStepIcon {...props} icon={index + 1} />
              )}
            >
              <Typography sx={{ 
                fontWeight: 600, 
                color: activeStep >= index ? '#1e293b' : '#94a3b8',
                fontSize: { xs: '0.7rem', sm: '0.875rem', md: '1rem' },
                display: { xs: isMobile ? 'block' : 'none', sm: 'block' },
              }}>
                {label}
              </Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ mb: { xs: 4, sm: 5, md: 6 } }}>
        {getStepContent(activeStep)}
      </Box>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column-reverse', sm: 'row' },
        justifyContent: 'space-between', 
        gap: { xs: 2, sm: 0 },
        pt: { xs: 3, sm: 4 },
        borderTop: '1px solid #f1f5f9'
      }}>
        <Button 
          onClick={handleBack}
          disabled={activeStep === 0 || isLoading}
          fullWidth={isMobile}
          sx={{ 
            fontSize: { xs: "0.875rem", md: "1rem" },
            px: { xs: 3, md: 4 },
            py: { xs: 1.25, md: 1.75 },
            fontWeight: 600,
            borderRadius: "50px",
            textTransform: "none",
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            borderWidth: 2,
            border: `2px solid ${theme.palette.primary.main}`,
            "&:hover": {
              borderWidth: 2,
              bgcolor: `${theme.palette.primary.main}15`,
              borderColor: theme.palette.primary.light,
            },
            "&:disabled": {
              borderColor: theme.palette.action.disabled,
              color: theme.palette.action.disabled,
            }
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={handleNext}
          disabled={isLoading}
          fullWidth={isMobile}
          sx={{
            fontSize: { xs: "0.875rem", md: "1rem" },
            px: { xs: 4, md: 5 },
            py: { xs: 1.25, md: 1.75 },
            fontWeight: 700,
            borderRadius: "50px",
            textTransform: "none",
            boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
            position: "relative",
            overflow: "hidden",
            '&::before': {
              content: '""',
              position: "absolute",
              top: 0,
              left: "-100%",
              width: "100%",
              height: "100%",
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
              transition: "left 0.5s",
            },
            '&:hover': {
              bgcolor: theme.palette.primary.light,
              transform: 'translateY(-4px) scale(1.02)',
              boxShadow: '0 16px 56px rgba(0,0,0,0.2)',
              '&::before': {
                left: '100%'
              }
            },
            '&:disabled': {
              bgcolor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
              boxShadow: 'none',
              transform: 'none',
            },
            transition: "all 0.3s ease"
          }}
        >
          <span style={{ position: "relative", zIndex: 1 }}>
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : activeStep === steps.length - 1 ? (
              'Create Account'
            ) : (
              'Continue'
            )}
          </span>
        </Button>
      </Box>

      <Snackbar 
        open={openSnackbar} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            maxWidth: { xs: '90vw', sm: 'auto' },
          }
        }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity} 
          sx={{ 
            width: '100%',
            borderRadius: '12px',
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            '&.MuiAlert-standardError': {
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#991b1b'
            },
            '&.MuiAlert-standardSuccess': {
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              color: '#166534'
            }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AllOnboard;
